import React, { useState } from 'react';
import Link from 'next/link';
import { pushAutoEvent } from '@/utils/gtag';
import { ArrowRightOutlined, CarOutlined, FieldTimeOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Button } from 'antd';

interface TSAAirportData {
  wait_time: number;
  airport_name: string;
  city_name?: string;
}

function getDotClass(mins: number) {
  if (mins < 10) return "dot-short";
  if (mins < 20) return "dot-moderate";
  if (mins < 30) return "dot-busy";
  return "dot-very-busy";
}

const LongestLinesList2 = ({ airports }) => {
  const sortedAirports = Object.entries(airports || {})
    .filter(([, data]) => typeof (data as TSAAirportData).wait_time === 'number')
    .sort(([, a], [, b]) => (b as TSAAirportData).wait_time - (a as TSAAirportData).wait_time)
    .slice(0, 10);
  const [showAll, setShowAll] = useState(false);


  return (
    <section className="container mx-auto p-5 z-10 space-y-2 md:space-y-4">
      <h2 className="font-display text-lg md:text-xl font-bold text-foreground mb-3">Longest Lines Right Now</h2>
      <ul className="space-y-2 md:space-y-0 md:divide-y md:divide-border">
        {sortedAirports.map(([code, data], index) => {
          const { airport_name, wait_time } = data as TSAAirportData;
          return (
            <li key={code}>
              <Link
                href={`/airport-security-wait-times/${code}`}
                className={`grid grid-cols-[minmax(0,1fr)_74px] items-center gap-2 py-2.5 md:py-3 px-3 md:px-2 rounded-xl md:rounded-lg bg-muted/30 md:bg-transparent border border-border/50 md:border-0 min-h-[52px] ${!showAll && index >= 5 ? "hidden md:grid" : "grid"}`}
                onClick={() => {
                  pushAutoEvent("tsa", `newTSA-goToTSA-${code}`, "click");
                }}
              >
                <div className="min-w-0">
                  <div className="flex flex-col">
                    <p className="text-sm font-medium text-foreground truncate">
                      {airport_name} <span className="text-muted-foreground">({code})</span></p>
                    {wait_time >= 30 && (
                      <p className="text-[10px] text-red-500 font-medium mt-0.5">⚡ High demand — book {code} parking before it fills up</p>
                    )}
                  </div>
                </div>
                <div className="flex w-[74px] items-center justify-end gap-2 shrink-0">
                  <span className="text-sm font-bold text-foreground text-right">{wait_time} min</span>
                  <span className={`h-3 w-3 rounded-full ${wait_time <= 9
                    ? 'bg-[#15803d]'
                    : wait_time <= 19
                      ? 'bg-[#3b82f6]'
                      : wait_time <= 29
                        ? 'bg-[#fbbf24]'
                        : 'bg-[#b91c1c]'
                    } `} />
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
      {!showAll && (
        <button onClick={() => setShowAll(true)} className="md:hidden w-full mt-2 py-3 text-sm font-medium text-primary rounded-xl border border-border/50 bg-muted/20">
          Show all {sortedAirports.length} airports
        </button>
      )}

      <div className="mt-4 rounded-xl border border-primary/20 p-4"
        style={{ backgroundImage: 'linear-gradient(to right, #FBF5F1, white)' }}>
        <div className="flex flex-col md:flex-row items-center gap-3 md:gap-6">
          <div className="flex items-center gap-3 flex-1 text-center md:text-left">
            <div className="hidden md:flex h-10 w-10 rounded-full bg-destructive/10 items-center justify-center shrink-0">
              <FieldTimeOutlined className="text-xl text-destructive" />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">⏰ Spots filling fast at top airports</p>
              <p className="text-xs text-muted-foreground mt-0.5">Guaranteed rate. Free shuttle. Cancel anytime.</p>
            </div>
          </div>
          <Link
            type="link"
            href="/airports"
            onClick={() => {
              pushAutoEvent("tsa", `newTSA-goToAirportsPage`, "click");
            }}
          >
            <Button type="primary" size="large" className="w-full md:w-auto gap-2 h-12 md:h-10 text-base md:text-sm shadow-md shadow-primary/20 rounded-xl">
              <CarOutlined /> Lock In My Price <ArrowRightOutlined />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default LongestLinesList2;
